<div class="cd-card">
  <div class="cd-prefs-bar">
    <div class="cd-prefs-heading">
      <h1>Meeting Preferences</h1>
      <span class="cd-prefs-subtitle">How and when students can book with you</span>
    </div>

    <div class="cd-prefs-actions">
      @if (editing()) {
        <button
          matButton
          class="cd-btn cd-btn-cancel"
          (click)="cancel()"
          data-testid="preferences-cancel-button"
        >
          Cancel
        </button>
        <button
          matButton
          class="cd-btn cd-btn-save"
          [disabled]="hasErrors()"
          (click)="save()"
          data-testid="preferences-save-button"
        >
          Save
        </button>
      } @else {
        <button
          matButton
          class="cd-btn cd-btn-edit"
          (click)="edit()"
          data-testid="preferences-edit-button"
        >
          <mat-icon>edit</mat-icon>
          Edit
        </button>
      }
    </div>
  </div>

  @if (editing() && hasErrors()) {
    <p class="cd-prefs-error" role="alert" data-testid="preferences-error-banner">
      {{ saveBlockedReason() }}
    </p>
  }

  <!-- ══ CARDS ═══════════════════════════════════════════════════════════ -->
  <div class="cd-prefs-grid">
    <section class="cd-module">
      <app-counselor-availability [(days)]="weeklyAvailability" [editing]="editing()" />
    </section>

    <div class="cd-prefs-side">
      <!-- Meetings per day -->
      <section class="cd-module">
        <div class="cd-card-header">
          <div class="cd-header-text">
            <div class="cd-header-content">
              <mat-icon>event_available</mat-icon>
              <h2>Meetings per Day</h2>
            </div>
            <span class="cd-subtitle">Cap your daily bookings</span>
          </div>
        </div>
        <div class="cd-module-body">
          @if (editing()) {
            <mat-form-field class="cd-number-field" subscriptSizing="dynamic">
              <input
                matInput
                type="number"
                min="1"
                [max]="maxMeetingsPerDay"
                placeholder="No limit"
                [ngModel]="meetingsPerDay()"
                (ngModelChange)="meetingsPerDay.set($event)"
                data-testid="meetings-per-day-input"
              />
            </mat-form-field>
          } @else if (meetingsPerDay() !== null) {
            <span class="cd-module-value">{{ meetingsPerDay() }}</span>
            <span class="cd-module-label">meetings per day</span>
          } @else {
            <span class="cd-module-unset">No limit set</span>
          }
        </div>
      </section>

      <!-- Meeting lengths -->
      <section class="cd-module">
        <div class="cd-card-header">
          <div class="cd-header-text">
            <div class="cd-header-content">
              <mat-icon>hourglass_top</mat-icon>
              <h2>Meeting Lengths</h2>
            </div>
            <span class="cd-subtitle">Durations students can book</span>
          </div>
        </div>
        <div class="cd-module-body cd-module-chips">
          @if (editing()) {
            @for (option of durationOptions; track option) {
              <button
                type="button"
                class="cd-chip cd-chip-toggle"
                [class.cd-chip-on]="allowedDurations().includes(option)"
                [attr.aria-pressed]="allowedDurations().includes(option)"
                (click)="toggleDuration(option)"
                [attr.data-testid]="'duration-chip-' + option"
              >
                {{ option }} min
              </button>
            }
          } @else {
            @for (duration of allowedDurations(); track duration) {
              <span class="cd-chip">{{ duration }} min</span>
            } @empty {
              <span class="cd-module-unset">No durations set</span>
            }
          }
        </div>
      </section>

      <!-- Where meetings happen -->
      <section class="cd-module">
        <div class="cd-card-header">
          <div class="cd-header-text">
            <div class="cd-header-content">
              <mat-icon>place</mat-icon>
              <h2>Location</h2>
            </div>
            <span class="cd-subtitle">Where you meet</span>
          </div>
        </div>
        <div class="cd-module-body cd-module-stack">
          @if (editing()) {
            <mat-form-field class="cd-text-field" subscriptSizing="dynamic">
              <input
                matInput
                placeholder="Room 204"
                [ngModel]="inPersonLocation()"
                (ngModelChange)="inPersonLocation.set($event)"
                data-testid="in-person-location-input"
              />
            </mat-form-field>
            <mat-form-field class="cd-text-field" subscriptSizing="dynamic">
              <mat-select
                [ngModel]="onlinePlatform()"
                (ngModelChange)="onlinePlatform.set($event)"
                placeholder="Online platform"
                data-testid="online-platform-select"
              >
                <mat-option [value]="null">None</mat-option>
                <mat-option value="Google Meet">Google Meet</mat-option>
                <mat-option value="Microsoft Teams">Microsoft Teams</mat-option>
                <mat-option value="Zoom">Zoom</mat-option>
              </mat-select>
            </mat-form-field>
          } @else {
            @if (inPersonLocation()) {
              <div class="cd-module-line">
                <mat-icon class="cd-module-icon">meeting_room</mat-icon>
                <span>{{ inPersonLocation() }}</span>
              </div>
            }
            @if (onlinePlatform()) {
              <div class="cd-module-line">
                <mat-icon class="cd-module-icon">videocam</mat-icon>
                <span>{{ onlinePlatform() }}</span>
              </div>
            }
            @if (!inPersonLocation() && !onlinePlatform()) {
              <span class="cd-module-unset">No location set</span>
            }
          }
        </div>
      </section>

      <!-- Add meeting instructions -->
      <section class="cd-module">
        <div class="cd-card-header">
          <div class="cd-header-text">
            <div class="cd-header-content">
              <mat-icon>info_outline</mat-icon>
              <h2>Additional Information</h2>
            </div>
            <span class="cd-subtitle"
              >Add instructions or information students should know before a meeting</span
            >
          </div>
        </div>

        <div class="cd-module-body cd-module-stack">
          @if (editing()) {
            <mat-form-field class="cd-text-field" subscriptSizing="dynamic">
              <textarea
                matInput
                rows="4"
                [maxlength]="maxInstructionsLength"
                placeholder="e.g. Check in at the front desk when you arrive"
                [ngModel]="instructions()"
                (ngModelChange)="instructions.set($event)"
                data-testid="instructions-input"
              ></textarea>
              <mat-hint align="end">
                {{ (instructions() ?? '').length }}/{{ maxInstructionsLength }}
              </mat-hint>
            </mat-form-field>
          } @else if (instructions()) {
            <span class="cd-module-instructions" data-testid="instructions-value">{{
              instructions()
            }}</span>
          } @else {
            <span class="cd-module-unset">No instructions set</span>
          }
        </div>
      </section>
    </div>
  </div>

  <!-- ══ BLOCK OFF TIME ══════════════════════════════════════════════════ -->
  <div class="cd-prefs-full">
    <section class="cd-module">
      <div class="cd-card-header">
        <div class="cd-header-text">
          <div class="cd-header-content">
            <mat-icon>event_busy</mat-icon>
            <h2>Block Off Time</h2>
          </div>
          <span class="cd-subtitle">Override your weekly hours for specific dates</span>
        </div>
        @if (editing() && !showBlockForm()) {
          <button
            matButton
            class="cd-btn cd-btn-edit"
            (click)="showBlockForm.set(true)"
            data-testid="add-block-button"
          >
            <mat-icon>add</mat-icon>
            Add block
          </button>
        }
      </div>

      <div class="cd-module-body cd-module-stack cd-block-body">
        @if (showBlockForm()) {
          <div class="cd-block-form" data-testid="block-form">
            <div class="cd-block-type-toggle">
              <button
                type="button"
                class="cd-chip cd-chip-toggle"
                [class.cd-chip-on]="blockType() === 'single'"
                [attr.aria-pressed]="blockType() === 'single'"
                (click)="blockType.set('single')"
                data-testid="block-type-single-button"
              >
                Single day
              </button>
              <button
                type="button"
                class="cd-chip cd-chip-toggle"
                [class.cd-chip-on]="blockType() === 'range'"
                [attr.aria-pressed]="blockType() === 'range'"
                (click)="blockType.set('range')"
                data-testid="block-type-range-button"
              >
                Range of days
              </button>
            </div>

            <div class="cd-block-form-row">
              @if (blockType() === 'single') {
                <mat-form-field class="cd-text-field" subscriptSizing="dynamic">
                  <mat-label>Date</mat-label>
                  <input
                    matInput
                    readonly
                    [matDatepicker]="blockDatePicker"
                    [min]="earliestDate"
                    [ngModel]="blockDate()"
                    (ngModelChange)="blockDate.set($event)"
                    (click)="blockDatePicker.open()"
                    data-testid="block-date-input"
                  />
                  <mat-datepicker-toggle matIconSuffix [for]="blockDatePicker" />
                  <mat-datepicker #blockDatePicker />
                </mat-form-field>
              } @else {
                <mat-form-field class="cd-text-field" subscriptSizing="dynamic">
                  <mat-label>Start date</mat-label>
                  <input
                    matInput
                    readonly
                    [matDatepicker]="blockRangeStartPicker"
                    [min]="earliestDate"
                    [ngModel]="blockRangeStart()"
                    (ngModelChange)="blockRangeStart.set($event)"
                    (click)="blockRangeStartPicker.open()"
                    data-testid="block-range-start-input"
                  />
                  <mat-datepicker-toggle matIconSuffix [for]="blockRangeStartPicker" />
                  <mat-datepicker #blockRangeStartPicker />
                </mat-form-field>
                <span class="cd-range-separator" aria-hidden="true">to</span>
                <mat-form-field class="cd-text-field" subscriptSizing="dynamic">
                  <mat-label>End date</mat-label>
                  <input
                    matInput
                    readonly
                    [matDatepicker]="blockRangeEndPicker"
                    [min]="earliestDate"
                    [ngModel]="blockRangeEnd()"
                    (ngModelChange)="blockRangeEnd.set($event)"
                    (click)="blockRangeEndPicker.open()"
                    data-testid="block-range-end-input"
                  />
                  <mat-datepicker-toggle matIconSuffix [for]="blockRangeEndPicker" />
                  <mat-datepicker #blockRangeEndPicker />
                </mat-form-field>
              }
            </div>

            @if (blockType() === 'single') {
              <div class="cd-block-form-row cd-block-time-row">
                <mat-checkbox
                  [ngModel]="blockAllDay()"
                  (ngModelChange)="blockAllDay.set($event)"
                  data-testid="block-all-day-checkbox"
                >
                  All day
                </mat-checkbox>

                @if (!blockAllDay()) {
                  <mat-form-field class="cd-time-slot" subscriptSizing="dynamic">
                    <input
                      matInput
                      [matTimepicker]="blockStartTimePicker"
                      [matTimepickerMin]="earliestTime"
                      [ngModel]="blockStartTime()"
                      (ngModelChange)="blockStartTime.set($event)"
                      placeholder="9:00 AM"
                      data-testid="block-start-time-input"
                    />
                    <mat-timepicker-toggle matIconSuffix [for]="blockStartTimePicker" />
                    <mat-timepicker #blockStartTimePicker />
                  </mat-form-field>
                  <span class="cd-range-separator" aria-hidden="true">to</span>
                  <mat-form-field class="cd-time-slot" subscriptSizing="dynamic">
                    <input
                      matInput
                      [matTimepicker]="blockEndTimePicker"
                      [matTimepickerMin]="earliestTime"
                      [ngModel]="blockEndTime()"
                      (ngModelChange)="blockEndTime.set($event)"
                      placeholder="3:00 PM"
                      data-testid="block-end-time-input"
                    />
                    <mat-timepicker-toggle matIconSuffix [for]="blockEndTimePicker" />
                    <mat-timepicker #blockEndTimePicker />
                  </mat-form-field>
                }
              </div>
            }

            <mat-form-field class="cd-text-field" subscriptSizing="dynamic">
              <mat-label>Reason (optional)</mat-label>
              <input
                matInput
                placeholder="e.g. Out of office, Conference"
                [ngModel]="blockReason()"
                (ngModelChange)="blockReason.set($event)"
                data-testid="block-reason-input"
              />
            </mat-form-field>

            <div class="cd-block-form-actions">
              <button
                matButton
                class="cd-btn cd-btn-cancel"
                (click)="cancelBlockForm()"
                data-testid="block-form-cancel-button"
              >
                Cancel
              </button>
              <button
                matButton
                class="cd-btn cd-btn-save"
                [disabled]="!blockFormValid()"
                (click)="addBlock()"
                data-testid="block-form-add-button"
              >
                Add Block
              </button>
            </div>
          </div>
        }

        <div class="cd-block-list">
          @for (block of blockedPeriods(); track block.id) {
            <div class="cd-block-item" [attr.data-testid]="'blocked-period-' + block.id">
              <mat-icon class="cd-module-icon">event_busy</mat-icon>
              <div class="cd-block-item-info">
                <span class="cd-block-item-dates">
                  @if (isSingleDay(block)) {
                    {{ block.startDate | date: 'EEE, MMM d, y' }}
                    @if (!block.allDay) {
                      &nbsp;·&nbsp;{{ block.startTime | date: 'h:mm a' }} –
                      {{ block.endTime | date: 'h:mm a' }}
                    }
                  } @else {
                    {{ block.startDate | date: 'MMM d' }} –
                    {{ block.endDate | date: 'MMM d, y' }}
                  }
                </span>
                @if (block.reason) {
                  <span class="cd-block-item-reason">{{ block.reason }}</span>
                }
              </div>
              @if (editing()) {
                <button
                  matIconButton
                  class="cd-remove-btn"
                  matTooltip="Remove"
                  (click)="removeBlock(block.id)"
                  [attr.data-testid]="'remove-blocked-period-' + block.id"
                >
                  <mat-icon>close</mat-icon>
                </button>
              }
            </div>
          } @empty {
            <span class="cd-module-unset">No blocked periods</span>
          }
        </div>
      </div>
    </section>
  </div>
</div>
